import { closestCorners, DndContext, DragOverlay } from '@dnd-kit/core';
import { Empty, Skeleton } from 'antd';

import { useClientsAccessRights } from '@app/hooks';
import type { HorizontalScrollModel } from '@app/shared/hooks';
import { ScrollMinimap } from '@app/shared/ui';
import type { MinimapColumn } from '@app/shared/ui';

import { LeadsFunnelLeadCard } from '../../LeadsFunnelLeadCard';
import { LeadsFunnelStageColumn } from '../../LeadsFunnelStageColumn';
import { LeadsFunnelStageColumnSkeleton } from '../../LeadsFunnelStageColumnSkeleton';
import {
  LEADS_FUNNEL_STAGE_COLUMN_GAP,
  useLeadsFunnelBoardColumns,
} from '../hooks/useLeadsFunnelBoardColumns';

import type { LeadsFunnelDndModel } from '../../../../hooks/useLeadsFunnelDnd';
import type { LeadsFunnelBoardModel } from '../../../../model/types';
import type { CSSProperties, FC, RefCallback } from 'react';

import './LeadsFunnelBoard.scss';

interface LeadsFunnelBoardProps {
  board: LeadsFunnelBoardModel;
  dnd: LeadsFunnelDndModel;
  hasMoreLeads: boolean;
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  onLoadMore(): void;
}

interface RenderInitialSkeletonParams {
  columnWidth: number;
  scrollRef: RefCallback<HTMLDivElement>;
  trackWidth: number;
  visibleCount: number;
}

const getColumnsStyle = (
  columnWidth: number,
  trackWidth: number
): CSSProperties =>
  ({
    '--leads-funnel-stage-column-gap': `${LEADS_FUNNEL_STAGE_COLUMN_GAP}px`,
    '--leads-funnel-stage-column-width': `${columnWidth}px`,
    '--leads-funnel-stage-track-width': `${trackWidth}px`,
  } as CSSProperties);

const renderInitialSkeleton = ({
  columnWidth,
  scrollRef,
  trackWidth,
  visibleCount,
}: RenderInitialSkeletonParams) => {
  const columnStride = columnWidth + LEADS_FUNNEL_STAGE_COLUMN_GAP;

  return (
    <div className="LeadsFunnelBoard__columns-shell">
      <div
        className="LeadsFunnelBoard__columns"
        ref={scrollRef}
        style={getColumnsStyle(columnWidth, trackWidth)}
      >
        <div className="LeadsFunnelBoard__columns-track">
          {Array.from({ length: visibleCount }, (_, index) => (
            <div
              key={`leads-funnel-skeleton-${index}`}
              className="LeadsFunnelBoard__columns-item"
              style={{ left: index * columnStride }}
            >
              <LeadsFunnelStageColumnSkeleton />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const LeadsFunnelBoard: FC<LeadsFunnelBoardProps> = ({
  board,
  dnd,
  hasMoreLeads,
  isInitialLoading,
  isLoadingMore,
  onLoadMore,
}) => {
  const isDragActive = dnd.activeLead !== null;
  // Право читается один раз на доску и уходит в карточки пропом (issue/11538).
  const { hasUserRightToShowContacts } = useClientsAccessRights();
  const {
    containerRef,
    scrollRef,
    virtualStages,
    columnWidth,
    totalTrackWidth,
    skeletonVisibleCount,
    skeletonColumnWidth,
    skeletonTrackWidth,
    hasHorizontalOverflow,
    viewportWidth,
    scrollLeft,
    onHorizontalScroll,
    onScrollTo,
  } = useLeadsFunnelBoardColumns({ stages: board.stages, isDragActive });

  const scrollModel: HorizontalScrollModel = {
    contentWidth: totalTrackWidth,
    viewportWidth,
    scrollLeft,
    onScrollTo,
  };

  const minimapColumns: MinimapColumn[] = board.stages.map((stage, index) => ({
    id: stage.key,
    offset: index * (columnWidth + LEADS_FUNNEL_STAGE_COLUMN_GAP),
    width: columnWidth,
  }));

  if (isInitialLoading) {
    return (
      <div className="LeadsFunnelBoard" ref={containerRef}>
        <div className="LeadsFunnelBoard__summary">
          <Skeleton.Input active size="small" />
          <Skeleton.Input active size="small" />
        </div>
        {renderInitialSkeleton({
          columnWidth: skeletonColumnWidth,
          scrollRef,
          trackWidth: skeletonTrackWidth,
          visibleCount: skeletonVisibleCount,
        })}
      </div>
    );
  }

  const hasStages = board.stages.length > 0;

  const boardContent = (
    <div className="LeadsFunnelBoard" ref={containerRef}>
      <div className="LeadsFunnelBoard__summary">
        <div className="LeadsFunnelBoard__summary-main">
          <span className="LeadsFunnelBoard__label">Воронка</span>
          <span className="LeadsFunnelBoard__title">
            {board.summary.funnelName}
          </span>
        </div>

        <div className="LeadsFunnelBoard__summary-side">
          <div className="LeadsFunnelBoard__summary-meta">
            <span>{board.summary.stagesCount} этапов</span>
            <span>
              {board.summary.loadedLeadsCount}
              {board.summary.totalLeadsCount !== null
                ? ` из ${board.summary.totalLeadsCount}`
                : ''}{' '}
              лидов
            </span>
          </div>
        </div>
      </div>

      {hasStages ? (
        <div className="LeadsFunnelBoard__columns-shell">
          <div
            className="LeadsFunnelBoard__columns"
            ref={scrollRef}
            style={getColumnsStyle(columnWidth, totalTrackWidth)}
            aria-label="Этапы воронки"
            onScroll={onHorizontalScroll}
          >
            <div className="LeadsFunnelBoard__columns-track">
              {virtualStages.map(({ stage, offsetLeft }) => (
                <div
                  key={stage.key}
                  className="LeadsFunnelBoard__columns-item"
                  style={{ left: offsetLeft }}
                >
                  <LeadsFunnelStageColumn
                    stage={stage}
                    canShowContacts={hasUserRightToShowContacts}
                    isDragEnabled={dnd.isDragEnabled}
                    isDragActive={isDragActive}
                    hasMoreLeads={hasMoreLeads}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={onLoadMore}
                  />
                </div>
              ))}
            </div>
          </div>

          {hasHorizontalOverflow && (
            <ScrollMinimap
              className="LeadsFunnelBoard__minimap"
              model={scrollModel}
              columns={minimapColumns}
            />
          )}
        </div>
      ) : (
        <div className="LeadsFunnelBoard__empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Нет этапов для выбранных фильтров"
          />
        </div>
      )}
    </div>
  );

  if (!dnd.isDragEnabled) {
    return boardContent;
  }

  return (
    <DndContext
      sensors={dnd.sensors}
      collisionDetection={closestCorners}
      onDragStart={dnd.onDragStart}
      onDragOver={dnd.onDragOver}
      onDragEnd={dnd.onDragEnd}
      onDragCancel={dnd.onDragCancel}
    >
      {boardContent}
      <DragOverlay>
        {dnd.activeLead && (
          <LeadsFunnelLeadCard
            lead={dnd.activeLead}
            canShowContacts={hasUserRightToShowContacts}
            funnelId={board.stages[0]?.funnelId ?? null}
            stageId={null}
            isOverlay
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};
